import { Avatar, Box, IconButton, Tooltip, Typography } from "@mui/material";
import RichTextEditor from "../../Common/RichTextEditor";
import { useEffect, useState } from "react";
import parse from "html-react-parser";
import { NoteData } from "../../../utils/types";
import React from "react";
import EditIcon from "../../../assets/action-items/edit.svg";
import DeleteIcon from "../../../assets/interaction-detail-page/notes-delete.svg";
import HoverDeleteIcon from "../../../assets/interaction-detail-page/idp-notes-delete.svg";
import CopySvg from "../../../assets/interaction-detail-page/notes-copy.svg";
import HoverNotesCopy from "../../../assets/interaction-detail-page/notes-copy-hover.svg";
import { dateFromatter, extractNameFromEmail, getAvatarColor, getAvatarInitials } from "../../../utils/utils";
import HoverEditIcon from "../../../assets/interaction-detail-page/edit-hover.svg";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";

interface NoteTileProps {
  note: NoteData;
  onUpdate: (noteId: string, updatedContent: string) => void;
  onDelete: (note: NoteData, event: any) => void;
  isEditable: boolean;
}

const NoteViewer : React.FC<NoteTileProps> = ({
  note,
  onUpdate,
  onDelete,
  isEditable,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(note?.notes || "");
  const [isEditHover, setIsEditHover] = useState(false);
  const [isCopyHover, setIsCopyHover] = useState(false);
  const [isDeleteHover, setIsDeleteHover] = useState(false);
  const { showSnackbar } = useDisplaySnackbar();

  useEffect(() => {
    setContent(note?.notes || "");
    setIsEditing(false);
  }, [note]);

  const handleSave = () => {
    onUpdate(note.id, content);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setContent(note?.notes || "");
    setIsEditing(false);
  };

  const handleCopyNotes = () => {
    const text = note?.notes || "";

    // remove HTML tags if you want plain text
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = text;
    const plainText = tempDiv.innerText;

    navigator.clipboard.writeText(plainText);
    showSnackbar("success", "Copied Notes Successfully");
  };

  return (
    <Box className="p2p-slide-box">
        <Box className="p2p-slide-header-box">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={note?.pictureUrl || undefined}
              sx={{ 
                width: 24, 
                height: 24,
                bgcolor: !note?.pictureUrl ? getAvatarColor(note.creatorEmail) : undefined,
              }}
            >
              {!note?.pictureUrl && getAvatarInitials(note.creatorEmail)}
            </Avatar>
            <Typography className="p2p-slide-header-text">
              <span className="user_name">{extractNameFromEmail("", note.creatorEmail)}</span>
              <span style={{ margin: "0 8px", color: "#33333B" }}>|</span>
              <span style={{ marginLeft: 2,color: "#A6A6B2", fontSize: "14px" }}>
                {dateFromatter(note?.createdAt || "")}
              </span>
            </Typography>
          </Box>

            {isEditable && !isEditing && (
            <Box sx={{ display: "flex", gap: 1 }}>
                <>
                    <Tooltip title="Edit Notes" placement="top" arrow>
                    <IconButton onClick={() => setIsEditing(true)} disableRipple
                        onMouseEnter={() => setIsEditHover(true)}
                        onMouseLeave={() => setIsEditHover(false)}>
                        <img src={isEditHover ? HoverEditIcon :EditIcon} alt="edit" className="edit-icon"/>
                    </IconButton>
                    </Tooltip>

                    <Tooltip title="Copy Notes" placement="top" arrow>
                    <IconButton onClick={handleCopyNotes} 
                        disableRipple
                        onMouseEnter={() => setIsCopyHover(true)}
                        onMouseLeave={() => setIsCopyHover(false)}>
                        <img src={isCopyHover ? HoverNotesCopy : CopySvg} alt="edit" className="edit-icon"/>
                    </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Notes" placement="top" arrow>
                    <IconButton 
                        onClick={(event) => {
                            event.stopPropagation();
                            onDelete(note, event);
                        }}
                        disableRipple
                        onMouseEnter={() => setIsDeleteHover(true)}
                        onMouseLeave={() => setIsDeleteHover(false)}
                    >
                      <img
                        src={isDeleteHover ? HoverDeleteIcon : DeleteIcon}
                        className="edit-icon"
                        alt="delete"
                      />
                    </IconButton>
                    </Tooltip>
                </>
            </Box>
            )}
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", padding: "12px" }}>
            {isEditing ? (
            <RichTextEditor
                id={note?.id}
                content={content}
                updateOnState={true}
                isEditable={true}
                onContentUpdate={setContent}
                height="500px"
                autoFocus
            />
            ) : (
            <Box>{parse(note?.notes || "")}</Box>
            )}
        </Box>

        {isEditing && (
        <Box
          sx={{
            borderTop: "1px solid #eee",
            padding: "12px 16px",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            background: "#fff"
          }}
        >
            <button
                className="btn btn-purple-compact"
                onClick={() => handleSave()}
            >
                Save
            </button>
            <button
                className="btn btn-outlined-small"
                onClick={() => {
                handleCancel();
                }}
            >
                Cancel
            </button>
        </Box>
      )}
    </Box>
  );
};

export default NoteViewer;